import {ApiError, Cart, CountriesFactory, ICartPosition, ICartRebateVoucher, ICartMessage} from "../typescript-library";
import React from "react";
import * as Sentry from "@sentry/browser";
import i18n from "i18next";
import {findRootError} from "../components/checkout/steps/Summary/summaryErrors";
import {PossibleNetworkError} from "../components/cart/ShoppingCart/ShoppingCart";

export type CartErrorType = {
  message: string | React.ReactElement;
  itemId?: number;
  itemExists?: boolean;
  blockCheckout: boolean;
};

const NOT_VOUCHER_APPLICABLE = "NOT_VOUCHER_APPLICABLE";
export const NO_VOUCHER_APPLICABLE_ITEM_FOUND = "NO_VOUCHER_APPLICABLE_ITEM_FOUND";
export const NOT_ALL_ITEMS_VOUCHER_APPLICABLE = "NOT_ALL_ITEMS_VOUCHER_APPLICABLE";
export const VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED = "VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED";
export const VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED = "VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED";
export const NO_SHIPPING_SELECTED = "NO_SHIPPING_SELECTED";
export const DELIVERY_ADDRESS_NOT_ALLOWED = "DELIVERY_ADDRESS_NOT_ALLOWED";
export const VOUCHER_IS_EXPIRED = "VOUCHER_IS_EXPIRED";
export const INVALID_VAT_NUMBER = "INVALID_VAT_NUMBER";

/**
 * Extracts error messages out of the cart object as strings or objects
 * with some additional data and also decorates the existing messages
 * of erroneous positions with some locally produced error messages
 * @param cart
 */
export const retrieveErrorMessages = (cart: Cart): CartErrorType[] => {
  const invalidPositions = cart.positions.filter((pos) => pos.messages.some((msg) => msg.type === "error"));
  const invalidRebates = cart.rebates.filter((rebate) => rebate.messages.some((msg) => msg.type === "error"));
  const country = CountriesFactory.getCountryByCode(cart.countryCode);

  if (!!invalidPositions.length || !!invalidRebates.length) {
    Sentry.setExtra("positions_with_error_messages", {
      deliveryCountry: cart.countryCode,
      errorPositions: invalidPositions.map((pos) => pos.messages.map((msg) => msg.code).join(" ")),
      errorRebates: invalidRebates.map((pos) => pos.messages.map((msg) => msg.code).join(" ")),
    });
  }

  // reducing to translated error strings out of positions[].messages[]
  const errorStrings = invalidPositions.reduce<CartErrorType[]>((accu, position) => {
    const voucher = position.reference?.voucher ?? null;
    const minimumOrderValue = voucher?.minimumOrderValueAbsolute?.formatted;
    const minimumQuantity = voucher?.minimumOrderQuantityAbsolute;
    const minimumQuantityMissing = voucher?.minimumOrderQuantityMissing;
    const voucherCode = voucher?.code || position.reference?.voucherCode || "";
    const {item} = position;
    const itemId = position.reference?.itemId;

    if (position.type === "Voucher") {
      return accu;
    }

    const msgs = position.messages.map((msg) => {
      return {
        message: i18n.t(msg.code, {
          ns: "errorcodes",
          itemId: item?.itemId ?? itemId ?? position.text,
          voucherCode,
          minimumOrderValue,
          minimumQuantity,
          count: minimumQuantityMissing,
          country: country?.name,
        }),
        blockCheckout: true,
        itemExists: !!item,
        itemId,
      } as CartErrorType;
    });

    if (msgs.length) {
      accu = accu.concat(msgs);
    }

    return accu;
  }, []);

  // reducing to translated error strings out of rebates[].messages[]
  const rebateErrors = cart.rebates.reduce<CartErrorType[]>((accumulator, current) => {
    const minimumOrderValue = current.minimumOrderValueAbsolute?.formatted ?? "";
    const voucherCode = current.code;
    const msgs = current.messages.map((msg) => ({
      message: i18n.t(msg.code, {minimumOrderValue, voucherCode, ns: "errorcodes"}),
      blockCheckout: true,
    }));

    return msgs.length ? accumulator.concat(msgs) : accumulator;
  }, []);

  // reducing to translated error strings out of cart.messages[]
  const cartObjectErrors = cart.messages.reduce<CartErrorType[]>((accumulator, current) => {
    // message code 'NO_SHIPPING_SELECTED' already covered by message
    // code 'SHIPPING_NOT_FOUND' in position of type 'Shipping'
    if (current.code !== NO_SHIPPING_SELECTED) {
      accumulator.push({
        message: i18n.t(current.code, {country, ns: "errorcodes"}) ?? "",
        blockCheckout: current.code !== INVALID_VAT_NUMBER,
      });
    }

    return accumulator;
  }, []);

  // region enriching messages of positions that are not applicable to certain vouchers

  const itemPositions = cart.positions.filter((pos) => !!pos.reference?.itemId);
  const fixVouchers = cart.positions.filter<ICartPosition>(
    (pos: ICartPosition): pos is ICartPosition => pos.type === "Voucher" && pos.reference?.voucher?.type === "Fix",
  );

  const relativeVouchers = cart.positions.filter<ICartPosition>(
    (pos: ICartPosition): pos is ICartPosition => pos.type === "Voucher" && pos.reference?.voucher?.type === "Relative",
  );

  itemPositions.forEach((itemPos) => {
    const {messages, discountability} = itemPos;
    const {isFixedVoucherApplicable, isRelativeVoucherApplicable} = discountability;

    const vouchersAndBooleans: [ICartPosition[], boolean][] = [
      [fixVouchers, isFixedVoucherApplicable],
      [relativeVouchers, isRelativeVoucherApplicable],
    ];

    vouchersAndBooleans.forEach(([vouchers, applicable]) => {
      if (applicable) {
        return;
      }

      vouchers.forEach((voucherPos) => {
        // if no error msg on position push one for specific voucher
        // if msg addressing that voucher already present, then ignore
        // just preventing double messages on re-rendering
        if (!messages.some((msg) => msg.field === (voucherPos.reference?.voucher?.code ?? ""))) {
          messages.push({
            code: NOT_VOUCHER_APPLICABLE,
            field: voucherPos.reference?.voucher?.code ?? "",
            message: "",
            type: "error",
          });
        }
      });
    });
  });

  const relativeVoucherApplicableItems = itemPositions.filter<ICartPosition>(
    (pos): pos is ICartPosition => pos.discountability.isRelativeVoucherApplicable,
  );
  const fixedVoucherApplicableItems = itemPositions.filter<ICartPosition>(
    (pos): pos is ICartPosition => pos.discountability.isFixedVoucherApplicable,
  );

  /*
   * fill applicability messages to cart positions of type voucher
   */
  const voucherPositions = [
    ...cart.rebates,
    ...fixVouchers.map((fv) => fv.reference?.voucher),
    ...relativeVouchers.map((rv) => rv.reference?.voucher),
  ];
  voucherPositions.forEach((voucher: ICartRebateVoucher) => {
    const {type, messages} = voucher;
    if (type === "Fix") {
      // fixed voucher applicability
      if (!fixedVoucherApplicableItems.length) {
        // not any applicable item found
        messages.push({code: NO_VOUCHER_APPLICABLE_ITEM_FOUND, field: "", message: "", type: "error"});
      } else if (itemPositions.length !== fixedVoucherApplicableItems.length) {
        // some, but not every item applicable
        messages.push({code: NOT_ALL_ITEMS_VOUCHER_APPLICABLE, field: "", message: "", type: "warning"});
      }
      // relative voucher applicability
    } else if (type === "Relative") {
      // relative voucher applicability
      if (!relativeVoucherApplicableItems.length) {
        // not any applicable item found
        messages.push({code: NO_VOUCHER_APPLICABLE_ITEM_FOUND, field: "", message: "", type: "error"});
      } else if (itemPositions.length !== relativeVoucherApplicableItems.length) {
        // some, but not every item applicable
        messages.push({code: NOT_ALL_ITEMS_VOUCHER_APPLICABLE, field: "", message: "", type: "warning"});
      }
      // else, all items are applicable (no message added)
    }
  });

  // endregion

  return errorStrings.concat(rebateErrors, cartObjectErrors);
};

export const expressPaymentsError = (
  expressError: ApiError | PossibleNetworkError,
  occurence?: string,
  additional?: Record<string, unknown>,
): {errorArray: string[]; sentryEventId: string} => {
  const tempError = ((expressError as ApiError).originalError || expressError) as PossibleNetworkError;

  if (__DEVELOPMENT__) {
    // eslint-disable-next-line no-console
    console.log("DEVELOPMENT ERROR:");
    // eslint-disable-next-line no-console
    console.error(tempError);
  }

  let errorArray: string[] = [];
  const {response} = tempError;
  // if response is defined, it's a network error
  if (response) {
    if (response.data.messages?.length) {
      errorArray = response.data.messages.map((msg: ICartMessage) => msg.code);
    } else if (response.data.result) {
      errorArray = [response.data.result.code || response.data.result];
    } else if (response.data.detail) {
      errorArray = [response.data.detail];
    }
  } else {
    const rootError = findRootError(tempError) as Error & {code?: string | undefined};
    if (rootError.code) {
      errorArray = [rootError.code];
    } else if (rootError.message) {
      errorArray = [rootError.message];
    }
  }

  const section = document.querySelector("#ModalRootElement > section");
  if (section) {
    if (typeof section.scrollTo === "function") {
      section.scrollTo({left: 0, top: 0, behavior: "auto"});
    } else {
      section.scrollTop = 0;
      section.scrollLeft = 0;
    }
  }

  const consent = document.cookie.split("CookieConsent=")[1];
  const sentryEventId = Sentry.captureException(tempError, {
    extra: {
      place: occurence ?? "express checkout return error in cart view",
      error: tempError,
      cookieConsent: consent?.split(";")[0],
      ...additional,
    },
  });

  // Error structure absolutely unknown
  if (!errorArray.length) {
    // dummy message, just to get to the _DEFAULT_ERROR_MESSAGE_ fallback below
    errorArray.push("no_error_message_found");
  }

  return {errorArray, sentryEventId};
};
